import React, { useState, useRef } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ReplayIcon from '@mui/icons-material/Replay';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import AudioPlayer from './AudioPlayer';

interface VoiceRecorderProps {
  onUpload: (audioBlob: Blob) => Promise<void>;
  uploading: boolean;
  existingUrl?: string;
}

const VoiceRecorder: React.FC<VoiceRecorderProps> = ({ onUpload, uploading, existingUrl }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [error, setError] = useState('');

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const startRecording = async () => {
    try {
      setError('');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(t => t.stop());
      };
      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      timerRef.current = setInterval(() => setRecordingTime(p => p + 1), 1000);
    } catch {
      setError('Mic access denied');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    }
  };

  const handleUpload = async () => {
    if (audioBlob) {
      await onUpload(audioBlob);
      setAudioBlob(null); setAudioUrl(null); setRecordingTime(0);
    }
  };

  const reset = () => { setAudioBlob(null); setAudioUrl(null); setRecordingTime(0); };
  const fmt = (s: number) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  const playbackSrc = audioUrl || existingUrl;
  const hasNewRecording = !!audioBlob;

  // ── Single row layout: [label] [player] [action] ──
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minHeight: 40 }}>
      {/* Left: label icon + text */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
        <GraphicEqIcon sx={{ fontSize: 14, color: "text.disabled" }} />
        <Typography sx={{ fontFamily: "'Libre Baskerville', Georgia, serif", fontSize: "0.8rem", color: "text.primary" }}>
          Voice
        </Typography>
      </Box>

      {/* Center: player or status */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {isRecording ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
            <Box sx={{
              width: 8, height: 8, borderRadius: "50%", backgroundColor: "primary.main", flexShrink: 0,
              animation: "pulse 1.5s infinite",
              "@keyframes pulse": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.3 } },
            }} />
            <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "0.68rem", color: "primary.main", fontWeight: 500 }}>
              {fmt(recordingTime)}
            </Typography>
          </Box>
        ) : playbackSrc ? (
          <AudioPlayer src={playbackSrc} />
        ) : (
          <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "0.65rem", color: "text.disabled" }}>
            No recording yet
          </Typography>
        )}
        {error && (
          <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "0.6rem", color: "error.main" }}>
            {error}
          </Typography>
        )}
      </Box>

      {/* Right: action buttons */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.3, flexShrink: 0 }}>
        {/* Save new recording */}
        {hasNewRecording && !isRecording && (
          <Box
            onClick={uploading ? undefined : handleUpload}
            sx={{
              p: 0.5, cursor: uploading ? "default" : "pointer",
              color: "primary.main", transition: "all 0.2s",
              "&:hover": { opacity: 0.7 },
              display: "flex", alignItems: "center",
            }}
          >
            {uploading
              ? <CircularProgress size={14} sx={{ color: "primary.main" }} />
              : <CloudUploadIcon sx={{ fontSize: 14 }} />
            }
          </Box>
        )}

        {/* Discard new recording */}
        {hasNewRecording && !isRecording && !uploading && (
          <Box
            onClick={reset}
            sx={{
              p: 0.5, cursor: "pointer", color: "text.disabled",
              transition: "all 0.2s", "&:hover": { color: "text.primary" },
              display: "flex", alignItems: "center",
            }}
          >
            <ReplayIcon sx={{ fontSize: 14 }} />
          </Box>
        )}

        {/* Record / Stop */}
        <Box
          onClick={isRecording ? stopRecording : startRecording}
          sx={{
            width: 28, height: 28, borderRadius: "50%",
            backgroundColor: isRecording ? "primary.main" : "divider",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", transition: "all 0.2s",
            "&:hover": { backgroundColor: isRecording ? "primary.dark" : "primary.main",
              "& .mic-icon": { color: "#faf8f5" },
            },
          }}
        >
          {isRecording
            ? <StopIcon sx={{ fontSize: 12, color: "#faf8f5" }} />
            : <MicIcon className="mic-icon" sx={{ fontSize: 12, color: "#faf8f5", transition: "color 0.2s" }} />
          }
        </Box>
      </Box>
    </Box>
  );
};

export default VoiceRecorder;
